import React, { useState, useEffect, useRef, Fragment } from 'react'
import { StyleSheet, css } from 'aphrodite'
import { FormControl, Input, MenuItem, Select, TextField } from '@material-ui/core'
import { Helper } from '../common/helpers/Helper'
import { AppTheme } from '../styles/AppTheme'
import { AppButton } from '../common/components/AppButton'
import { ApiHelper } from '../common/helpers/ApiHelper'
import { SingleSelectDropDown } from '../common/components/SingleSelectDropDown'
const { v4: uuidv4 } = require('uuid');

const LayoutEditor = (props) => {
  const outerRef = useRef(null)
  const groupId = props.orgId
  const usedColors = []
  const MAX_OVERLAY_ALLOWED = 2

  const PRESET_RESOLUTIONS = [
    { label: 'HD 1080p', width: 1920, height: 1080 },
    { label: 'HD 720p', width: 1280, height: 720 },
    { label: '4K UHD', width: 3840, height: 2160 },
    { label: 'Custom Layout', width: 1920, height: 1080 }
  ]

  // const PRESET_ORIENTATION = [
  //   { label: 'LANDSCAPE', VALUE: 'LANDSCAPE' },
  //   { label: 'PORTRAIT', VALUE: 'PORTRAIT' }
  // ]

  const PRESET_STATE = [
    { LABEL: 'ENABLED', VALUE: 'LIVE' },
    { LABEL: 'DISABLED', VALUE: 'DISABLED' }
  ]
  // Minimum Size of a section
  const sectionMinSize = 100
  // Scale down is the factor by which lasge layout is disabled in current view port.
  const [scaleDown, setScaleDown] = useState(1)
  const [overlayEnabled, setOverlayEnabled] = useState(false)

  const [layoutName, setLayoutName] = useState('')
  // const [orientation, setOrientation] = useState(PRESET_ORIENTATION[0].label)
  // const [resolution, setResolution] = useState(PRESET_RESOLUTIONS[0].label)
  const [layoutType, setLayoutType] = useState('ML')
  const [durationType, setDurationType] = useState('LIMITED')
  const [durationSecs, setDurationSecs] = useState('')

  const [sections, setSections] = useState([])
  const [layoutDim, setLayoutDim] = useState(PRESET_RESOLUTIONS[0])

  const [selectedId, setSelectedId] = useState(null)
  // const [settingsChangeable, setSettingsChangeable] = useState(false)

  const [layoutState, setLayoutState] = useState(PRESET_STATE[0].VALUE)
  const [message, setMessage] = useState('')
  const [loading, setLoading] = useState(false)
  const [maxOverlayAdded, setMaxOverlayAdded] = useState(false)
  const [fieldsDisable, setFieldsDisable] = useState(false)
  // Reload the structure from listing.
  useEffect(() => {
    if (
      props.layoutJson &&
      props.layoutJson.layoutjson &&
      props.layoutJson.layoutjson.sections &&
      props.layoutJson.layoutjson.sections.length > 0
    ) {
      const layoutJson = props.layoutJson
      setLayoutDim({
        width: layoutJson.width,
        height: layoutJson.height
      })

      setLayoutName(layoutJson.title || '')
      setSections(layoutJson.layoutjson.sections || [])
      setLayoutState(layoutJson.state || PRESET_STATE[0].VALUE)
      setDurationSecs(layoutJson.durationSecs || '')
      setDurationType(layoutJson.durationType || 'LIMITED')
      setLayoutType(layoutJson.layoutType || 'ML')
      if(layoutJson.createdTs) setFieldsDisable(true)
    }
  }, [props.layoutJson])

  useEffect(() => {
    checkSectionsComplete()
    if (sections.length > 0 && selectedId === null) {
      setSelectedId(sections[0].id)
    }
    calculateScale()
  }, [sections, selectedId, layoutDim])

  // Calculate scaleDown by comparing outer container width vs. layoutDim.width
  // This function takes the current viewport dimension. for say 600x600, and returns the scale factor keeping the ratio of original layout
  const calculateScale = () => {
    if (outerRef.current && layoutDim) {
      const containerWidth = outerRef.current.clientWidth;
      const containerHeight = outerRef.current.clientHeight;

      // Calculate scale for both dimensions
      const scaleByWidth = containerWidth / layoutDim.width;
      const scaleByHeight = containerHeight / layoutDim.height;

      // Choose the smaller scale to ensure the layout fits
      const newScale = Math.min(scaleByWidth, scaleByHeight, 1);
      setScaleDown(newScale);
    }
  }

  // Return a scaled copy of sections for rendering
  // This function is used to render sections inside layout
  // This returns scaled down section, by the factor received from calculateScale function.
  const getScaledSections = () => {
    return sections.map((sec) => {
      return {
        ...sec,
        scaledX: sec.x * scaleDown,
        scaledY: sec.y * scaleDown,
        scaledWidth: sec.width * scaleDown,
        scaledHeight: sec.height * scaleDown,
      }
    })
  }

  // When we add a new section, we used a random color.
  // This function generated color randomly, keeping a stack which color is generated, and not generate again.
  function getRandomColor() {
    if (usedColors.length >= 500000) {
      throw new Error('All possible lighter colors have been used!')
    }

    let foundColor = false
    let color = ''

    while (!foundColor) {
      const r = 150 + Math.floor(Math.random() * 106)
      const g = 150 + Math.floor(Math.random() * 106)
      const b = 150 + Math.floor(Math.random() * 106)

      color = `rgba(${r}, ${g}, ${b}, 0.8)`

      if (!usedColors.includes(color)) {
        usedColors.push(color)
        foundColor = true
      }
    }

    return color
  }

  const isComponentMaxedOut = (type, maxAllowedNumbers) => {
    if(!sections) return
    const noOfAddedCompoenent = sections.filter(x => x.type === type).length
    return (noOfAddedCompoenent >= maxAllowedNumbers) ?  true : false
  }

  // This function Adds layout/overly on ui
  // also it checks if there is space available to add section/overly, and if no space is available then shows error message
  const addNewItem = (width, height, type) => {
    // Check Maximum overlay
    if(type === 'overlay' && isComponentMaxedOut(type,MAX_OVERLAY_ALLOWED)) {
      const maxOutMessage = Helper.getString('maxOverlayMsg')
      setMessage(maxOutMessage.replace('MAX_OVERLAY_NUMBER', MAX_OVERLAY_ALLOWED))
      return
    }

    let x = 0
    let y = 0
    while (!isSpaceAvailable(x, y, width, height, null, type)) {
      x += width
      if (x + width > layoutDim.width) {
        x = 0
        y += sectionMinSize
      }
      if (y + sectionMinSize > layoutDim.height) {
        setMessage(Helper.getString('noSpaceToAddSection'))
        return
      }
    }
    const zIndex = type === 'overlay' ? 2 : 1
    const backgroundColor = getRandomColor()
    const newItem = {
      x,
      y,
      width,
      height,
      id: uuidv4(),
      type,
      zIndex,
      backgroundColor,
      media: []
    }
    setSections((prev) => [...prev, newItem])
    setSelectedId(newItem.id)
    setMessage('')
  }

  // This is a trigger function. which get triggered from ui. and call new item function for adding a new section/overlay.
  const addSection = (width, height) => {
    addNewItem(width, height, 'section')
  }

  const addOverlay = (width, height) => {
    addNewItem(width, height, 'overlay')
  }

  // This function iterate each section, and check the current section is not colliding others
  // Also checks if the sections/overlay dimension not not more that layout's dimension
  const isSpaceAvailable = (x, y, width, height, excludeId = null, itemType) => {
    // check if current item not nor going out of layout dimension
    if (x + width > layoutDim.width || y + height > layoutDim.height || x < 0 || y < 0) {
      return false
    }
    // check current items coordinates are not colliding others
    for (const item of sections) {
      if (item.id === excludeId) continue
      if (itemType === 'section') {
        if (item.type === 'section') {
          if (
            x < item.x + item.width &&
            x + width > item.x &&
            y < item.y + item.height &&
            y + height > item.y
          ) {
            return false
          }
        }
      } else if (itemType === 'overlay') {
        if (item.type === 'overlay') {
          if (
            x < item.x + item.width &&
            x + width > item.x &&
            y < item.y + item.height &&
            y + height > item.y
          ) {
            return false
          }
        }
      }
    }
    return true
  }

  // Update a perticular sections x, y, width, hight, by finding it by id
  const updateSectionData = (id, newX, newY, newWidth, newHeight) => {
    setSections((prev) =>
      prev.map((section) =>
        section.id === id
          ? { ...section, x: newX, y: newY, width: newWidth, height: newHeight }
          : section
      )
    )
  }

  // This function checks where the section total area and same as the layout area's
  // This function is is used to trigger auto fill and disabling/actions action like, add section, remove section, auto fill
  const checkSectionsComplete = () => {
    let totalArea = 0
    for (const section of sections) {
      if (section.type === 'section') {
        totalArea += section.width * section.height
      }
    }
    setOverlayEnabled(totalArea >= layoutDim.width * layoutDim.height)
    getMaxOverlayAdded()
  }

  // This function generate the speed factor which we multiply with the delta number of drag mouse movement, to match up screen movement with scaled downed layout's sections
  const _getSpeedFactorDragNResize = () => {
    let speedFactor = 3
    if (layoutDim && layoutDim.height && layoutDim.width) {
      if (layoutDim.height > layoutDim.width) speedFactor = layoutDim.height / outerRef.current.clientHeight
      if (layoutDim.height < layoutDim.width) speedFactor = layoutDim.width / outerRef.current.clientWidth
    }
    return Math.round(speedFactor)
  }

  // This function make the drag of sections/overlay
  const handleDrag = (id, e) => {
    // Clear any selection, to avoid auto drag bug
    clearTextSelection()

    const item = sections.find((sec) => sec.id === id)
    if (!item) return

    // Start positions in screen coordinates ( Current mouse position)
    let startX = e.clientX
    let startY = e.clientY

    // Remember the original item.x, item.y
    const initialX = item.x
    const initialY = item.y

    // Variables to store last updated X & Y positions
    let lastUpdatedX = initialX
    let lastUpdatedY = initialY

    const onMouseMove = (e) => {
      // Calculate delta in screen coordinates
      const deltaXscreen = e.clientX - startX
      const deltaYscreen = e.clientY - startY

      // Update startX and startY for the next calculation
      startX = e.clientX
      startY = e.clientY

      // Speed multiplied to match the screen delta to scaled downed delta.
      const dragSensitivity = _getSpeedFactorDragNResize()
      const deltaXoriginal = deltaXscreen * dragSensitivity
      const deltaYoriginal = deltaYscreen * dragSensitivity

      // 1st this finds out, how much it can move and how much it moved. if it moved way too much, then take allowed moved number otherwise actual moved number
      // Math.min(layoutDim.width - item.width, lastUpdatedX + deltaXoriginal)
      // Math.min(1920 - 300 = 1060, 100 + 500 = 600) ::: final output 600 (this section moved with in the layout area)
      // Math.max(0,600) = 600
      // Math.min(1920 - 300 = 1060, 100 + 2800 = 2900)  = 1060 (this section moved beyond the layout area in right, so will take max allowed)
      // Math.max(0,1060) = 1600

      // Handle Negative delta, if move mouse at left out side layout
      // 1) Math.min(1920 - 300 = 1060, 100 + -2800 = -2700)  = -2700 (this section moved beyond the layout area in right, so will take max allowed right position)
      // Math.max(0,-2700) = 0 // this Limits max left movement. to stop section go out of layout
      const newX = Math.max(0, Math.min(layoutDim.width - item.width, lastUpdatedX + deltaXoriginal))
      const newY = Math.max(0, Math.min(layoutDim.height - item.height, lastUpdatedY + deltaYoriginal))

      // Update the position if space is available
      // Is space available check if a divs width is more that the dimension width then returns false and blocks more incrementing
      // Also it checks if the layout is not colliding others, if its going to collide it reruns false and stops incensing.
      if (isSpaceAvailable(newX, newY, item.width, item.height, id, item.type)) {
        updateSectionData(id, newX, newY, item.width, item.height)
        lastUpdatedX = newX
        lastUpdatedY = newY
      } else {
        // Attempt to adjust the position along each axis separately if possible
        if (isSpaceAvailable(newX, lastUpdatedY, item.width, item.height, id, item.type)) {
          updateSectionData(id, newX, lastUpdatedY, item.width, item.height)
          lastUpdatedX = newX
        }
        if (isSpaceAvailable(lastUpdatedX, newY, item.width, item.height, id, item.type)) {
          updateSectionData(id, lastUpdatedX, newY, item.width, item.height)
          lastUpdatedY = newY
        }
      }
    }

    // Clears the event of section
    const onMouseUp = () => {
      clearTextSelection()
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
    }

    // Set up event listeners for mouse movement and release
    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)
  }

  // This function takes care of resizing a section/overlay
  const handleResize = (id, e) => {
    // Prevent the event from bubbling up the DOM and clear any text selection
    e.stopPropagation()
    clearTextSelection()

    // Find the section based on the id provided
    const item = sections.find((sec) => sec.id === id)
    if (!item) return  // Exit if no section is found

    // Set initial mouse coordinates ( on screen where mouse is)
    let startX = e.clientX
    let startY = e.clientY

    // current items width and height
    let lastUpdatedWidth = item.width
    let lastUpdatedHeight = item.height

    const onMouseMove = e => {
      // Calculate the difference from the initial mouse position
      const deltaX = e.clientX - startX
      const deltaY = e.clientY - startY

      // Update start coordinates for the next movement calculation
      startX = e.clientX
      startY = e.clientY

      // Speed multiplied to match the screen delta to scaled downed delta.
      const speedFactor = _getSpeedFactorDragNResize()
      const widthIncrement = deltaX * speedFactor
      const heightIncrement = deltaY * speedFactor

      // Adjust width if space is available
      // This prevents a section to reduce its width or height less than the minimum width defined
      const newWidth = Math.min(layoutDim.width, Math.max(sectionMinSize, lastUpdatedWidth + widthIncrement))
      // Is space available check if a divs width is more that the dimension width then returns false and blocks more incrementing
      // Also it checks if the layout is not colliding others, if its going to collide it reruns false and stops incensing.
      if (isSpaceAvailable(item.x, item.y, newWidth, lastUpdatedHeight, id, item.type)) {
        updateSectionData(id, item.x, item.y, newWidth, lastUpdatedHeight)
        lastUpdatedWidth = newWidth
      }
      // Adjust height if space is available
      // This prevents a section to reduce its width or height less than the minimum height defined
      const newHeight = Math.min(layoutDim.height, Math.max(sectionMinSize, lastUpdatedHeight + heightIncrement))
      if (isSpaceAvailable(item.x, item.y, lastUpdatedWidth, newHeight, id, item.type)) {
        updateSectionData(id, item.x, item.y, lastUpdatedWidth, newHeight)
        lastUpdatedHeight = newHeight
      }
    }

    // Register and unregister mouse events to handle movement and stop resizing
    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', () => {
      document.removeEventListener('mousemove', onMouseMove)
    })
  }

  // This function clears any selection while dragging or resizing any sections.
  const clearTextSelection = () => {
    if (window.getSelection) { // Check if the method exists in the browser
      window.getSelection().removeAllRanges(); // Remove all ranges from the selection
    } else if (document.selection) { // Fallback for older IE
      document.selection.empty(); // Clear the selection
    }
  }

  // Resize section as per new resolution
  // const scaleSectionsToResolution = (newWidth, newHeight) => {
  //   const scaleX = newWidth / layoutDim.width
  //   const scaleY = newHeight / layoutDim.height

  //   const scaledSections = sections.map((section) => ({
  //     ...section,
  //     x: Math.round(section.x * scaleX),
  //     y: Math.round(section.y * scaleY),
  //     width: Math.round(section.width * scaleX),
  //     height: Math.round(section.height * scaleY),
  //   }))

  //   setSections(scaledSections)
  //   setLayoutDim({ ...layoutDim, width: newWidth, height: newHeight })
  // }

  // Resolution change
  // const handleResolutionChange = (event) => {
  //   const newLabel = event.target.value
  //   setResolution(newLabel)
  //   const found = PRESET_RESOLUTIONS.find((res) => res.label === newLabel)
  //   if (found) {
  //     // Resize section as per new resolution
  //     const newWidth = orientation === 'PORTRAIT' ? found.height : found.width
  //     const newHeight = orientation === 'PORTRAIT' ? found.width : found.height
  //     scaleSectionsToResolution(newWidth, newHeight)
  //   }
  // }
  // Orientation change
  // const handleOrientationChange = (event) => {
  //   const newOrientation = event.target.value
  //   setOrientation(newOrientation)
  //   setLayoutDim((prev) => {
  //     if (newOrientation === 'PORTRAIT') {
  //       return { ...prev, width: prev.height, height: prev.width }
  //     } else {
  //       return { ...prev, width: prev.height, height: prev.width }
  //     }
  //   })
  //   // On Change orientation clear the section
  //   setSections([])
  // }

  // State
  const handleStateChange = (event) => {
    const state = event.target.value
    setLayoutState(state)
  }

  // This function return how much space is available on the layout
  const getSpaceAvailableOnLayout = () => {
    let totalAreaFilled = 0
    for (const section of sections) {
      totalAreaFilled += section.width * section.height
    }
    return (layoutDim.width * layoutDim.height) - totalAreaFilled
  }

  // We still do the auto-fill in original coords
  const autoFill = () => {
    const layoutW = layoutDim.width
    const layoutH = layoutDim.height
    const secs = sections.filter((s) => s.type === 'section')
    const overlays = sections.filter((s) => s.type === 'overlay')
    if (!secs.length) return
    // Checks if a section is colliding other
    const collides = (x, y, w, h, excludeId) => {
      if (x < 0 || y < 0 || x + w > layoutW || y + h > layoutH) return true
      for (const sec of secs) {
        if (sec.id === excludeId) continue
        if (
          x < sec.x + sec.width &&
          x + w > sec.x &&
          y < sec.y + sec.height &&
          y + h > sec.y
        ) {
          return true
        }
      }
      return false
    }

    let loopCount = 0
    // Current empty space is the layout
    let availableAreaAfterAutofill = getSpaceAvailableOnLayout()
    while (loopCount < 3) { // Current empty space reains same for 3 times, stops auto filling
      // For each section it loops
      for (const element of secs) {
        const s = element
        // 1. Increase the width till it collide
        while (!collides(s.x, s.y, s.width + 1, s.height, s.id)) {
          s.width++
        }
        // 2. Increase the height till it collide
        while (!collides(s.x, s.y, s.width, s.height + 1, s.id)) {
          s.height++
        }
        // 3. Move toward left till it collide
        while (!collides(s.x, s.y - 1, s.width, s.height, s.id)) {
          s.y--
        }
        // 4. Move toward top till it collide
        while (!collides(s.x - 1, s.y, s.width, s.height, s.id)) {
          s.x--
        }
      }
      const newAreaAvailable = getSpaceAvailableOnLayout()
      // After doing for all the section. checked if empty space has difference
      if (newAreaAvailable !== availableAreaAfterAutofill) {
        // If has difference, means, auto filled worked, and can try again
        availableAreaAfterAutofill = newAreaAvailable
      } else {
        // If no difference, means, auto filled did not worked, Increased the counter, no in above check if its failed thrice. auto fill will stop
        loopCount++
      }
    }
    // Update the auto filled sections.
    setSections([...secs, ...overlays])
  }

  // This function first auto fill and then call apis to save the layout json
  const autoFillAndSave = () => {
    if (!layoutName || layoutName && layoutName.trim() === '') {
      setMessage(Helper.getString('layoutNameRequired'))
      return
    }
    if (layoutType === 'SL' && durationType === 'LIMITED' && durationSecs.trim() === '') {
      setMessage(Helper.getString('durationSecsRequired'))
      return
    }
    setLoading(true)
    autoFill()
    const newSection = [...sections]
    newSection.sort((a, b) => {
      // In case of overlay move it to last
      if (a.type === 'overlay' && b.type !== 'overlay') return 1
      if (a.type !== 'overlay' && b.type === 'overlay') return -1
      // First compare by y
      const yDiff = a.y - b.y
      if (yDiff !== 0) return yDiff
      // If y is the same, compare by x
      return a.x - b.x
    })

    const saveOutputJson = {
      groupId: groupId,
      title: layoutName,
      width: layoutDim.width,
      height: layoutDim.height,
      state: layoutState,
      layoutType: layoutType,
      mediaType: 'LAYOUT',
      layoutjson: {
        sections: [...newSection]
      }
    }
    if(layoutType === 'SL') saveOutputJson.durationType = durationType
    if(durationType === 'LIMITED') saveOutputJson.durationSecs = durationSecs

    let apiEndpoint = {}
    if (props.editMode && props.editMode === true && props.layoutId) {
      apiEndpoint = {
        method: 'put', paths: ['layouts', props.layoutId],
        jsonBody: { ...saveOutputJson }
      }
    } else {
      apiEndpoint = {
        method: 'post', paths: ['layouts'],
        jsonBody: { ...saveOutputJson }
      }
    }
    ApiHelper.call(apiEndpoint, (err, result) => {
      setLoading(false)
      if (result && result.id) {
        if (props.successCallback) props.successCallback();
        return
      }
      if (err || !result.id) {
        const errorMessage = Helper.getErrorMsg(err)
        setMessage(errorMessage)
        return
      }
    })
  }

  // this function prevents selection when dragging
  const preventSelection = (e) => {
    e.preventDefault()
  }

  // This function removes a section from layout
  const handleDeleteSelected = () => {
    if (!selectedId) return
    setSections((prev) => prev.filter((sec) => sec.id !== selectedId))
    setSelectedId(null)
  }

  // const handleCustomResolutionChange = (dimension, value) => {
  //   const newValue = parseInt(value, 10) || 0;
  //   setLayoutDim((prev) => {
  //     const updatedLayoutDim = { ...prev, [dimension]: newValue };
  //     return updatedLayoutDim;
  //   })
  //   // On Change orientation clear the section
  //   setSections([])
  // }

  const getMaxOverlayAdded = () => {
    if(isComponentMaxedOut('overlay',MAX_OVERLAY_ALLOWED)) {
      setMaxOverlayAdded(true)
    }else{
      setMaxOverlayAdded(false)
    }
  }

  const handleSectionSizeChange = (e) => {
    if (!selectedId) return;

    const { name, value } = e.target;
    const newValue = parseInt(value) || 0; // Default to minimum size if invalid
    const selectedSection = sections.find((sec) => sec.id === selectedId);

    if (!selectedSection) return;

    let newWidth = selectedSection.width;
    let newHeight = selectedSection.height;

    if (name === 'sectionWidth') {
      newWidth = Math.min(layoutDim.width, Math.max(0, newValue));
    } else if (name === 'sectionHeight') {
      newHeight = Math.min(layoutDim.height, Math.max(0, newValue));
    }

    // Validate new dimensions
    if (isSpaceAvailable(selectedSection.x, selectedSection.y, newWidth, newHeight, selectedId, selectedSection.type)) {
      updateSectionData(selectedId, selectedSection.x, selectedSection.y, newWidth, newHeight);
    } else {
      // setMessage('Cannot resize: New dimensions would cause collision or exceed layout boundaries.');
    }
  }

  const handleBlurSectionSize = () => {
    if (!selectedId) return;

    const section = sections.find((sec) => sec.id === selectedId);
    if (!section) return;

    const { x, y, width, height, type } = section;
    const updated = false;

    const newWidth = width < sectionMinSize
      ? Math.min(layoutDim.width, sectionMinSize)
      : width;

    const newHeight = height < sectionMinSize
      ? Math.min(layoutDim.height, sectionMinSize)
      : height;

    if (newWidth !== width || newHeight !== height) {
      if (isSpaceAvailable(x, y, newWidth, newHeight, selectedId, type)) {
        updateSectionData(selectedId, x, y, newWidth, newHeight);
      }
    }
  }

  const getSelectedSectionsDimension = () => {
    const dim = { width: 0, height: 0 }
    if(selectedId) {
      const selectedItem = sections.find(x => x.id === selectedId)
      dim.width = selectedItem.width
      dim.height = selectedItem.height
    }
    return dim
  }

  return (
    <Fragment>
      <div className={css(Styles.inputsHolder)}>
        <div className={css(Styles.inputs)}>
          <div className={css(Styles.container)}>
            <div className={css(Styles.textFieldContainer)}>
              <label className={css(Styles.labelTextField)}>Name</label>
              <FormControl className={css(Styles.formControl)} margin='normal' fullWidth>
                <TextField
                  // disabled={!settingsChangeable}
                  name='name'
                  placeholder='Layout Name'
                  onChange={(e) => setLayoutName(e.target.value)}
                  value={layoutName}
                  InputLabelProps={{
                    shrink: false, // Prevent label from shrinking to the top
                  }}
                  className={css(Styles.formControlDialog)}
                  InputProps={{
                    disableUnderline: true,
                    classes: {
                      root: css(Styles.textInputStylesDialog),
                      input: css(Styles.textInput),
                    },
                  }}
                />
              </FormControl>
            </div>
            <SingleSelectDropDown
              onSinglePickerFilter={(e) => setLayoutType(e.target.value)}
              singleSelectPicker={{
                title: 'Select Layout Type',
                options: [
                  {
                    key: 'ML',
                    value: 'Media Layout'
                  },
                  {
                    key: 'SL',
                    value:'Screen Layout'
                  }
                ],
                defaultValue: layoutType,
              }}
              inputContainer={Styles.formControlDialog}
              textInputStyles={Styles.textInputStylesDialog}
              disabled={fieldsDisable}
            />
            {layoutType === 'SL' &&
              <SingleSelectDropDown
                onSinglePickerFilter={(e) => setDurationType(e.target.value)}
                singleSelectPicker={{
                  title: 'Select Duration Type',
                  options: [
                    {
                      key: 'LIMITED',
                      value: 'LIMITED'
                    },
                    {
                      key: 'FOREVER',
                      value:'FOREVER'
                    }
                  ],
                  defaultValue: durationType,
                }}
                inputContainer={Styles.formControlDialog}
                textInputStyles={Styles.textInputStylesDialog}
              />
            }
            {layoutType === 'SL' && durationType === 'LIMITED' &&
              <div className={css(Styles.textFieldContainer)}>
                <label className={css(Styles.labelTextField)}>Duration Seconds</label>
                <FormControl className={css(Styles.formControl)} margin='normal' fullWidth>
                  <TextField
                    // disabled={!settingsChangeable}
                    placeholder='Duration Seconds'
                    onChange={(e) => setDurationSecs(e.target.value)}
                    value={durationSecs}
                    InputLabelProps={{
                      shrink: false, // Prevent label from shrinking to the top
                    }}
                    className={css(Styles.formControlDialog)}
                    InputProps={{
                      disableUnderline: true,
                      classes: {
                        root: css(Styles.textInputStylesDialog),
                        input: css(Styles.textInput),
                      },
                    }}
                  />
                </FormControl>
              </div>
            }

            <div className={css(Styles.textFieldContainer)}>
              <label className={css(Styles.labelTextField)}>State</label>
              <FormControl fullWidth>
                <Select
                  name='state'
                  value={layoutState}
                  onChange={handleStateChange}
                  MenuProps={{
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'center'
                    },
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: 'center'
                    },
                    getContentAnchorEl: null,
                    className: css(Styles.menuProps, Styles.inputStyle),
                    classes: {
                      paper: css(Styles.paperSelect)
                    }
                  }}
                  input={<Input disableUnderline={true} />}
                  inputProps={{
                    classes: {
                      root: css(Styles.textInputStylesDialog)
                    }
                  }}
                  className={css(Styles.formControlDialog)}
                >
                  {PRESET_STATE.map((item) => (
                    <MenuItem key={item.LABEL} value={item.VALUE}>
                      {item.LABEL}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>

            <div className={css(Styles.textFieldContainer)}>
              {/* <label className={css(Styles.labelTextField)}>Device Resolution</label> */}
              {/* <FormControl fullWidth>
                <Select
                  name='resolution'
                  value={resolution}
                  onChange={handleResolutionChange}
                  MenuProps={{
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'center'
                    },
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: 'center'
                    },
                    getContentAnchorEl: null,
                    className: css(Styles.menuProps, Styles.inputStyle),
                    classes: {
                      paper: css(Styles.paperSelect)
                    }
                  }}
                  input={<Input disableUnderline={true} />}
                  inputProps={{
                    classes: {
                      root: css(Styles.textInputStylesDialog)
                    }
                  }}
                  className={css(Styles.formControlDialog)}
                >
                  {PRESET_RESOLUTIONS.map((item) => (
                    <MenuItem key={item.label} value={item.label}>
                      {item.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl> */}
              {/* Custom Resolution */}
              {/* {resolution === 'Custom Layout' && */}
              {/* <label className={css(Styles.labelTextField)}>Resolution</label>
              <div className={css(Styles.customResHold)}>
                <div className={css(Styles.customResInput)}>
                  <TextField
                    // disabled={resolution !== 'Custom Layout' || !settingsChangeable}
                    disabled={true}
                    fullWidth
                    value={layoutDim.width}
                    // onChange={(e) => handleCustomResolutionChange('width', e.target.value)}
                    InputLabelProps={{
                      shrink: false, // Prevent label from shrinking to the top
                    }}
                    className={css(Styles.formControlDialog, Styles.disabledInput)}
                    InputProps={{
                      disableUnderline: true,
                      classes: {
                        root: css(Styles.textInputStylesDialog),
                        input: css(Styles.textInput, Styles.textCenter),
                      },
                    }}
                  />
                </div>
                <div>
                  <span className='material-icons-outlined'>close</span>
                </div>
                <div className={css(Styles.customResInput)}>
                  <TextField
                    // disabled={resolution !== 'Custom Layout' || !settingsChangeable}
                    disabled={true}
                    fullWidth
                    value={layoutDim.height}
                    // onChange={(e) => handleCustomResolutionChange('height', e.target.value)}
                    // variant='outlined'
                    InputLabelProps={{
                      shrink: false, // Prevent label from shrinking to the top
                    }}
                    className={css(Styles.formControlDialog, Styles.disabledInput)}
                    InputProps={{
                      disableUnderline: true,
                      classes: {
                        root: css(Styles.textInputStylesDialog),
                        input: css(Styles.textInput, Styles.textCenter),
                      },
                    }}
                  />
                </div>
              </div> */}
              {/* } */}
            </div>
            <div className={css(Styles.textFieldContainer)}>
              {/* Section Dimension */}
              <label className={css(Styles.labelTextField)}>Section Dimension</label>
              <div className={css(Styles.customResHold)}>
                <div className={css(Styles.customResInput)}>
                  <TextField
                    // disabled={true}
                    type='number'
                    onChange={handleSectionSizeChange}
                    onBlur={handleBlurSectionSize}
                    name={'sectionWidth'}
                    fullWidth
                    value={getSelectedSectionsDimension().width}
                    InputLabelProps={{
                      shrink: false, // Prevent label from shrinking to the top
                    }}
                    className={css(Styles.formControlDialog)}
                    InputProps={{
                      disableUnderline: true,
                      classes: {
                        root: css(Styles.textInputStylesDialog),
                        input: css(Styles.textInput, Styles.textCenter),
                      },
                    }}
                  />
                </div>
                <div>
                  <span className='material-icons-outlined'>close</span>
                </div>
                <div className={css(Styles.customResInput)}>
                  <TextField
                    // disabled={true}
                    type='number'
                    name={'sectionHeight'}
                    onChange={handleSectionSizeChange}
                    onBlur={handleBlurSectionSize}
                    fullWidth
                    value={getSelectedSectionsDimension().height}
                    InputLabelProps={{
                      shrink: false, // Prevent label from shrinking to the top
                    }}
                    className={css(Styles.formControlDialog)}
                    InputProps={{
                      disableUnderline: true,
                      classes: {
                        root: css(Styles.textInputStylesDialog),
                        input: css(Styles.textInput, Styles.textCenter),
                      },
                    }}
                  />
                </div>
              </div>
            </div>

            {/* {resolution !== 'Custom Layout' &&
            <div className={css(Styles.textFieldContainer)}>
              <label className={css(Styles.labelTextField)}>Orientation</label>
              <FormControl fullWidth>
                <Select
                  name='orientation'
                  value={orientation}
                  onChange={handleOrientationChange}
                  MenuProps={{
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'center'
                    },
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: 'center'
                    },
                    getContentAnchorEl: null,
                    className: css(Styles.menuProps, Styles.inputStyle),
                    classes: {
                      paper: css(Styles.paperSelect)
                    }
                  }}
                  input={<Input disableUnderline={true} />}
                  inputProps={{
                    classes: {
                      root: css(Styles.textInputStylesDialog)
                    }
                  }}
                  className={css(Styles.formControlDialog)}
                >
                  {PRESET_ORIENTATION.map((item) => (
                    <MenuItem key={item.label} value={item.label}>
                      {item.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            } */}
            <div className={css(Styles.actionButtonHolder)}>
              <span className={css(Styles.actionLinkStyle, overlayEnabled && Styles.disabledLink)} onClick={() => { if (!overlayEnabled) { addSection(sectionMinSize, sectionMinSize) } }}>
                <span className='material-icons-outlined'>add</span> {Helper.getString('newSection')}
              </span>
              {layoutType !== 'SL' &&
                <span className={css([Styles.actionLinkStyle, !overlayEnabled && Styles.disabledLink, maxOverlayAdded && Styles.disabledLink])} onClick={() => { if (overlayEnabled) { addOverlay(sectionMinSize, sectionMinSize) } }}>
                  <span className='material-icons-outlined'>add</span> {Helper.getString('newOverlay')}
                </span>
              }
              <span className={css(Styles.actionLinkStyle, Styles.deleteButtonStyle, (!selectedId || (sections && sections.length < 2)) && Styles.disabledLink)} onClick={handleDeleteSelected}>
                <span className='material-icons-outlined'>remove_circle_outline</span> {Helper.getString('deleteSelected')}
              </span>
              <span className={css(Styles.actionLinkStyle, overlayEnabled && Styles.disabledLink)} onClick={() => { if (!overlayEnabled) { autoFill() } }}>
                <span className='material-icons-outlined'>border_all</span> {Helper.getString('autoFill')}
              </span>
            </div>

            {/* <div>
              <p>Output</p>
              <code>
                <pre>
                  {JSON.stringify({...layoutDim, layoutName, orientation, layoutItems: sections}, null, 2)}
                </pre>
              </code>
            </div> */}

          </div>
        </div>

        {/* Outer container for measuring width */}
        <div style={{ width: '650px', height: '366px' }}>
          <div ref={outerRef} style={{ width: '100%', height: '100%' }}>
            <div className={css(Styles.layoutHolder)}>
              {/* The scaled layout container */}
              <div
                className={css(Styles.layout)}
                style={{
                  width: layoutDim.width * scaleDown,
                  height: layoutDim.height * scaleDown
                }}
                onMouseDown={preventSelection}
                onMouseMove={preventSelection}
              >
                {/* Scaled down section */}
                {getScaledSections().map((item) => (
                  <div
                    key={item.id}
                    className={css(
                      Styles.section,
                      item.id === selectedId && Styles.selectedItem
                    )}
                    style={{
                      left: item.scaledX,
                      top: item.scaledY,
                      width: item.scaledWidth,
                      height: item.scaledHeight,
                      zIndex: item.zIndex,
                      backgroundColor: item.backgroundColor
                    }}
                    onMouseDown={(e) => {
                      handleBlurSectionSize()
                      setSelectedId(item.id)
                      handleDrag(item.id, e)
                    }}
                  >
                    <div className={css(Styles.sizeText)}>
                      {`${item.width}x${item.height} ${item.type === 'overlay' ? ' (Overlay)' : ''}`}
                    </div>
                    <div
                      className={css(Styles.resizer)}
                      onMouseDown={(e) => handleResize(item.id, e)}
                    ></div>
                  </div>
                ))}
              </div>

              {/* <div className={css(Styles.actionButtons)}>
                <div>
                  <span
                    className={css(
                      Styles.actionLinkStyle,
                      overlayEnabled && Styles.disabledLink
                    )}
                    onClick={() => {
                      if (!overlayEnabled) {
                        addSectionAuto(sectionMinSize, sectionMinSize)
                      }
                    }}
                  >
                    {Helper.getString('newSection')}
                  </span>
                </div>
                <div>
                  <span
                    className={css(
                      Styles.actionLinkStyle,
                      !overlayEnabled && Styles.disabledLink
                    )}
                    onClick={() => {
                      if (overlayEnabled) {
                        addOverlayAuto(sectionMinSize, sectionMinSize)
                      }
                    }}
                  >
                    {Helper.getString('addOverlay')}
                  </span>
                </div>
                <div>
                  <span
                    className={css(
                      Styles.actionLinkStyle,
                      overlayEnabled && Styles.disabledLink
                    )}
                    onClick={() => {
                      if (!overlayEnabled) {
                        autoFill()
                      }
                    }}
                  >
                    {Helper.getString('autoFill')}
                  </span>
                </div>
              </div> */}
              <div className={css(Styles.errorMessage)}>{message}</div>
            </div>
          </div>
        </div>
      </div>
      <div className={css(Styles.modalFooter)}>
        <AppButton inProcess={loading} onClick={() => { autoFillAndSave() }} buttonText={'Save'} ></AppButton>
      </div>
    </Fragment>
  )
}

const Styles = StyleSheet.create({
  disabledInput: {
    backgroundColor: 'rgb(217, 217, 217)'
  },
  errorMessage: {
    textAlign: 'right',
    color: 'red',
    padding: 12
  },
  actionButtonHolder: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    marginTop: 10
  },
  inputStyle: {
    fontSize: 16,
    color: '#575757'
  },
  formControl: {
    marginTop: 0,
    marginBottom: 2
  },
  formControlDialog: {
    borderRadius: 8,
    border: 'none',
    background: '#F7F7F9',
    overflow: 'hidden',
    maxWidth: 'unset',
    marginTop: 0
  },
  textInputStylesDialog: {
    padding: '10px 14px',
    flex: 1,
    textAlign: 'left',
    color: '#222222',
    fontSize: 16,
    ':focus': {
      backgroundColor: '#F7F7F9'
    },
  },
  textInput: {
    padding: '4px 0px'
  },
  textCenter: {
    textAlign: 'center'
  },
  layoutHolder: {
    position: 'relative'
  },
  layout: {
    position: 'relative',
    backgroundColor: '#f4f4f4',
    margin: '0 auto'
  },
  section: {
    position: 'absolute',
    boxSizing: 'border-box',
    cursor: 'move',
    border: '1px solid transparent',
    overflow: 'hidden'
  },
  resizer: {
    width: '10px',
    height: '10px',
    backgroundColor: '#000',
    position: 'absolute',
    bottom: 0,
    right: 0,
    cursor: 'se-resize'
  },
  sizeText: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    textAlign: 'center',
    fontSize: 10,
    userSelect: 'none'
  },
  inputsHolder: {
    minWidth: 800,
    minHeight: 400,
    display: 'flex',
    gap: 20,
    alignItems: 'flex-start'
  },
  inputs: {
    flex: 1
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10
  },
  labelTextField: {
    fontSize: 14,
    color: 'rgb(18, 18, 18)',
    margin: '0px 0px 8px !important',
    fontWeight: 467
  },
  textFieldContainer: {
    display: 'flex',
    flexDirection: 'column'
  },
  textInputStyles: {
    padding: '14px 16px',
    flex: 1,
    textAlign: 'left',
    color: '#222222',
    fontSize: 16,
    border: 'none'
  },
  menuProps: {
    zIndex: 130000,
    marginTop: 5
  },
  actionLinkStyle: {
    color: AppTheme.primaryColor,
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '8px',
  },
  actionButtons: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-around',
    alignItems: 'center',
    padding: '16px 0px'
  },
  selectedItem: {
    border: '2px solid green'
  },
  deleteButtonStyle: {
    color: 'red',
    border: 'none',
    cursor: 'pointer',
    ':disabled': {
      backgroundColor: '#aaa',
      cursor: 'default'
    },
    padding: '0 !important'
  },
  disabledLink: {
    color: '#999',
    cursor: 'default',
    textDecoration: 'none',
    pointerEvents: 'none'
  },
  modalFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: '16px 18px 0px 16px'
  },
  customResHold: {
    display: 'flex',
    // paddingTop: '10px',
    maxWidth: '240px',
    alignItems: 'center',
    gap: 10
  }
})

export default LayoutEditor



// WEBPACK FOOTER //
// ./src/components/LayoutEditor.js